import { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { keycloak, initKeycloak, getToken, refreshToken, logout as keycloakLogout, getUserInfo } from '../../auth/keycloak'

interface User {
  id?: string
  email?: string
  name?: string
  firstName?: string
  lastName?: string
  roles: string[]
  tenantId?: string
  isOrgAdmin?: boolean
}

interface AuthContextType {
  isAuthenticated: boolean
  isLoading: boolean
  user: User | null
  token: string | undefined
  login: (returnUrl?: string) => void
  logout: () => void
  hasRole: (role: string) => boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isLoading, setIsLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [user, setUser] = useState<User | null>(null)
  const roleSignature = useRef('')
  const queryClient = useQueryClient()

  useEffect(() => {
    const init = async () => {
      try {
        const authenticated = await initKeycloak()
        setIsAuthenticated(authenticated)
        if (authenticated) {
          const nextUser = getUserInfo()
          roleSignature.current = [...nextUser.roles].sort().join(',')
          setUser(nextUser)
        }
      } catch (error) {
        console.error('Auth initialization failed:', error)
      } finally {
        setIsLoading(false)
      }
    }

    init()

    // Every successful rotation, regardless of who triggered it (this
    // interval or the API client's 401 handler), must refresh both the
    // token-derived user and the membership data the admin-access check
    // reads from React Query — otherwise a revoked org_admin role keeps
    // granting Administration access from cached membership data.
    keycloak.onAuthRefreshSuccess = () => {
      const nextUser = getUserInfo()
      const nextRoleSignature = [...nextUser.roles].sort().join(',')
      if (roleSignature.current !== nextRoleSignature) {
        roleSignature.current = nextRoleSignature
        queryClient.invalidateQueries()
      }
      setUser(nextUser)
      queryClient.invalidateQueries({ queryKey: ['currentUserDetail'] })
      queryClient.invalidateQueries({ queryKey: ['currentUserContext'] })
    }

    // Set up token refresh
    const refreshInterval = setInterval(() => {
      if (keycloak.authenticated) {
        refreshToken()
      }
    }, 60000)

    return () => {
      clearInterval(refreshInterval)
      keycloak.onAuthRefreshSuccess = undefined
    }
  }, [queryClient])

  const login = () => {
    keycloak.login()
  }

  const logout = () => {
    keycloakLogout()
    setIsAuthenticated(false)
    setUser(null)
  }

  const hasRole = (role: string): boolean => {
    return user?.roles.includes(role) || false
  }

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        user,
        token: getToken(),
        login,
        logout,
        hasRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
